import React, { useState, useEffect } from 'react';
import { RotateCcw, Search, Download, RefreshCw, AlertTriangle, X, CheckCircle, List, Check } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Button,
  Checkbox
} from '@mui/material';
import MainService from '../../services/MainService';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';

const ReturnApprovals = () => {
  const { language } = useLanguage();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedReturns, setSelectedReturns] = useState(new Set());

  useEffect(() => {
    loadReturns();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [searchTerm, returns]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadReturns = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.ClientReturnsForApprovals({});
      if (response && Array.isArray(response)) {
        const returnsWithChecked = response.map(r => ({ ...r, checked: false }));
        setReturns(returnsWithChecked);
        setFilteredReturns(returnsWithChecked);
      } else {
        setReturns([]);
        setFilteredReturns([]);
      }
    } catch (err) {
      console.error('Error loading returns:', err);
      setError('Failed to load returns');
      setReturns([]);
      setFilteredReturns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredReturns(returns);
      setCurrentPage(1);
      return;
    }

    const keyword = searchTerm.toLowerCase();
    const filtered = returns.filter(returnItem => 
      (returnItem.RefNo && returnItem.RefNo.toLowerCase().includes(keyword)) ||
      (returnItem.Employee?.Name && returnItem.Employee.Name.toLowerCase().includes(keyword)) ||
      (returnItem.Createdby?.UserName && returnItem.Createdby.UserName.toLowerCase().includes(keyword)) ||
      (returnItem.Status && returnItem.Status.toLowerCase().includes(keyword))
    );
    setFilteredReturns(filtered);
    setCurrentPage(1);
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allIds = new Set(paginatedReturns.map(r => r.Id));
      setSelectedReturns(allIds);
      setFilteredReturns(prev => prev.map(r => 
        paginatedReturns.some(pr => pr.Id === r.Id) ? { ...r, checked: true } : r
      ));
    } else {
      const pageIds = new Set(paginatedReturns.map(r => r.Id));
      setSelectedReturns(prev => {
        const newSet = new Set(prev);
        pageIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      setFilteredReturns(prev => prev.map(r => 
        paginatedReturns.some(pr => pr.Id === r.Id) ? { ...r, checked: false } : r
      ));
    }
  };

  const handleSelectReturn = (returnId, checked) => {
    setSelectedReturns(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(returnId);
      } else {
        newSet.delete(returnId);
        setSelectAll(false);
      }
      return newSet;
    });

    setFilteredReturns(prev => prev.map(r => 
      r.Id === returnId ? { ...r, checked } : r
    ));
  };

  const handleApproveReturns = async () => {
    if (selectedReturns.size === 0) {
      setError('Please select at least one return to approve');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const returnArray = Array.from(selectedReturns).map(id => ({ Id: id }));
      const response = await MainService.ApproveClientReturn(returnArray);
      
      if (response === 'Success' || response === true) {
        setSuccess(`Successfully approved ${selectedReturns.size} return(s)`);
        setSelectedReturns(new Set());
        setSelectAll(false);
        loadReturns();
      } else {
        setError(response || 'Failed to approve returns');
      }
    } catch (err) {
      console.error('Error approving returns:', err);
      setError('Failed to approve returns');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const exportToExcel = () => {
    if (filteredReturns.length === 0) {
      setError('No data to export');
      return;
    }

    const dataToExport = filteredReturns.map(returnItem => ({
      'Ref No': returnItem.RefNo || '',
      'Date': formatDate(returnItem.Date),
      'Total Orders': returnItem.TotalOrders || 0,
      'Status': returnItem.Status || '',
      'Employee': returnItem.Employee?.Name || '',
      'Created By': returnItem.Createdby?.UserName || '',
      'Clients': returnItem.ClientsNames?.join(', ') || ''
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Returns');
    XLSX.writeFile(wb, `ReturnApprovals_${new Date().toISOString().split('T')[0]}.xlsx`);
    setSuccess('Data exported to Excel successfully');
  };

  const paginatedReturns = filteredReturns.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const totalPages = Math.ceil(filteredReturns.length / pageSize);

  useEffect(() => {
    if (paginatedReturns.length > 0) {
      const allSelected = paginatedReturns.every(r => selectedReturns.has(r.Id));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedReturns, paginatedReturns]);

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center">
            <List className="w-6 h-6 text-[#46955A] mr-2" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{getPageHeading(language, 'returnApprovalsList')}</h1>
              <p className="text-sm text-gray-500 mt-1">Review and approve pending client returns</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <TextField
              size="small"
              placeholder="Search returns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="w-4 h-4 text-gray-400" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '300px',
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#46955A',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#46955A',
                  },
                },
              }}
            />
            <Tooltip title="Export to Excel">
              <IconButton
                onClick={exportToExcel}
                disabled={filteredReturns.length === 0}
                sx={{
                  color: '#46955A',
                  '&:hover': { backgroundColor: '#f0f9f4' },
                  '&:disabled': { color: '#9CA3AF' }
                }}
              >
                <Download className="w-5 h-5" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                onClick={loadReturns}
                disabled={loading}
                sx={{
                  color: '#46955A',
                  '&:hover': { backgroundColor: '#f0f9f4' }
                }}
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={32} sx={{ color: '#46955A' }} />
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Loading returns...</span>
            </Box>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '8px' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px', width: '40px' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px', width: '40px' }}>
                      <Checkbox
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        size="small"
                        sx={{
                          color: '#46955A',
                          '&.Mui-checked': {
                            color: '#46955A',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Ref No</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Total Orders</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Created By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedReturns.length > 0 ? (
                    paginatedReturns.map((returnItem, index) => (
                      <TableRow
                        key={returnItem.Id}
                        hover
                        sx={{
                          '&:hover': { backgroundColor: '#F9FAFB' },
                          backgroundColor: selectedReturns.has(returnItem.Id) ? '#f0f9f4' : 'transparent'
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.875rem', color: '#6B7280', padding: '12px' }}>
                          {(currentPage - 1) * pageSize + index + 1}
                        </TableCell>
                        <TableCell sx={{ padding: '12px' }}>
                          <Checkbox
                            checked={selectedReturns.has(returnItem.Id)}
                            onChange={(e) => handleSelectReturn(returnItem.Id, e.target.checked)}
                            size="small"
                            sx={{
                              color: '#46955A',
                              '&.Mui-checked': {
                                color: '#46955A',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827', padding: '12px' }}>
                          {returnItem.RefNo || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          {formatDate(returnItem.Date)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', padding: '12px' }}>
                          {returnItem.TotalOrders || 0}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          <span className="px-2 py-1 text-xs font-medium rounded" style={{ 
                            backgroundColor: returnItem.Status === 'STATUS DONE' ? '#D1FAE5' : '#FEE2E2',
                            color: returnItem.Status === 'STATUS DONE' ? '#065F46' : '#991B1B'
                          }}>
                            {returnItem.Status || 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          {returnItem.Employee?.Name || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          {returnItem.Createdby?.UserName || 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ padding: '40px' }}>
                        <div className="flex flex-col items-center justify-center text-gray-400">
                          <RotateCcw className="w-12 h-12 mb-3 opacity-50" />
                          <p className="text-sm font-medium">
                            {searchTerm ? 'No returns found matching your search' : 'No returns pending approval'}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-3 mt-4">
              <Button
                variant="outlined"
                startIcon={<RefreshCw className="w-4 h-4" />}
                onClick={loadReturns}
                disabled={loading}
                sx={{
                  borderColor: '#46955A',
                  color: '#46955A',
                  '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' },
                  '&:disabled': { borderColor: '#9CA3AF', color: '#9CA3AF' }
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Check className="w-4 h-4" />}
                onClick={handleApproveReturns}
                disabled={loading || selectedReturns.size === 0}
                sx={{
                  backgroundColor: '#46955A',
                  '&:hover': { backgroundColor: '#3d7d4f' },
                  '&:disabled': { backgroundColor: '#9CA3AF' }
                }}
              >
                Approve Returns ({selectedReturns.size})
              </Button>
            </div>

            {/* Pagination Footer */}
            {filteredReturns.length > 0 && (
              <div className="flex items-center justify-between mt-4 px-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredReturns.length)} of {filteredReturns.length} returns
                  </span>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(e.target.value);
                        setCurrentPage(1);
                      }}
                      sx={{
                        fontSize: '0.875rem',
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#46955A',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#46955A',
                          },
                        },
                      }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, value) => setCurrentPage(value)}
                  color="primary"
                  sx={{
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: '#46955A',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#3d7d4f',
                      },
                    },
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReturnApprovals;
